// Speaker height off the floor — the UI speaks in terms a person setting up
// the room actually cares about (how high the cabinet's BOTTOM sits above
// the floor), not the raw world Y the document stores (the cabinet's
// CENTRE — see editorState.ts's `defaultSpeakerY`, which computes centre
// heights per subkind, several of them well off the floor: a bookshelf on a
// stand, an overhead near the ceiling).
//
// It also owns the one height RULE the model has: a floor-standing cabinet
// (a tower or a subwoofer) stands on the floor. `seatOnFloor` is how the
// reducer enforces it on every edit that could move a speaker's height, so
// no card, drag or solver result can leave a tower floating at ear height.

import type { SpeakerSubkind } from './adminTypes';
import type { SpeakerEntry } from './editorState';

/**
 * Default cabinet height, metres, for a speaker with no `size` override.
 *
 * These are the `h` of `SPEAKER_SIZE` in `@roomtreatment/viewport-3d`, the
 * box every renderer draws. Core cannot import that table (viewport-3d
 * depends on core, not the other way), so viewport-3d's
 * `speakerGeometry.test.ts` pins the two equal for every subkind instead.
 *
 * Deliberately NOT `defaultSpeakerY`'s own internal table, which predates
 * this one and differs for the types that do not stand on the floor
 * (bookshelf 0.35, nearfield 0.30, overhead 0.20). The reducer goldens pin
 * `defaultSpeakerY`'s output, so it keeps its numbers.
 */
export const SPEAKER_CABINET_H: Record<SpeakerSubkind, number> = {
  subwoofer: 0.4,
  nearfield: 0.34,
  bookshelf: 0.36,
  floorstanding: 1.05,
  overhead: 0.12,
};

/** Towers and subwoofers stand on the floor: their height off the floor is
 *  always 0, so there is no height to choose. Every other type is placed at a
 *  height (on a stand, a shelf, the ceiling). */
export function standsOnFloor(subkind: SpeakerSubkind): boolean {
  return subkind === 'floorstanding' || subkind === 'subwoofer';
}

/** A speaker's cabinet height: its own size override, else its type's
 *  default box. */
export function speakerCabinetH(entry: Pick<SpeakerEntry, 'subkind' | 'size'>): number {
  return entry.size?.h ?? SPEAKER_CABINET_H[entry.subkind];
}

/**
 * The model invariant: a floor-standing speaker's cabinet bottom is on the
 * floor, so its centre is at `y = cabinet height / 2`. X and Z are kept.
 *
 * Returns `entry` itself, by reference, when nothing changes (any other type,
 * or a cabinet already on the floor), so a caller can apply it to every write
 * and still tell a real change from a no-op.
 */
export function seatOnFloor(entry: SpeakerEntry): SpeakerEntry {
  if (!standsOnFloor(entry.subkind)) return entry;
  const y = speakerCabinetH(entry) / 2;
  if (entry.position[1] === y) return entry;
  return { ...entry, position: [entry.position[0], y, entry.position[2]] };
}

/** World Y (cabinet centre) → height of the cabinet's bottom above the floor. */
export function bottomHeightFromCenterY(centerY: number, cabinetHeight: number): number {
  return centerY - cabinetHeight / 2;
}

/** Height of the cabinet's bottom above the floor → world Y (cabinet centre). */
export function centerYFromBottomHeight(bottomHeight: number, cabinetHeight: number): number {
  return bottomHeight + cabinetHeight / 2;
}

/** Clamps a bottom-referenced height into what the room can actually hold:
 *  no lower than the floor (0), no higher than leaves the cabinet's top
 *  through the ceiling (`roomHeight - cabinetHeight`). A cabinet taller than
 *  the room clamps to sitting on the floor rather than producing a
 *  negative range. */
export function clampBottomHeight(bottomHeight: number, cabinetHeight: number, roomHeight: number): number {
  const max = Math.max(0, roomHeight - cabinetHeight);
  return Math.min(Math.max(bottomHeight, 0), max);
}
